import { Breadcrumb, Button, Col, Form, Input, Row, message } from "antd";
import { DeleteOutlined, HomeOutlined } from "@ant-design/icons";
import { Content } from "antd/es/layout/layout";
import { useLocation, useNavigate } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { _adr } from "../../../../address";
import { useAPIPublic, useDealer } from "../../../../tools/api";
import { globalValue } from "../../../../reducer/serviceReducerGlobala";
import { complexDigitToman, delay, extractKitNo } from "../../../../tools/tools";
import KitRequestModal from "../request/kitRequestModal";

const REASON_INCOMPLETE = "شماره کیت صحیح نمی‌باشد.";
const REASON_DUPLICATE = "شماره کیت تکراری است.";

const AssignKitDetail = () => {
	const location = useLocation();
	const nav = useNavigate();
	const { assignKitToDealer } = useDealer();
	const { queryCurrentUser } = useAPIPublic();
	const dispatch = useDispatch();
	const { _currentUser } = useSelector((state) => state?.another);

	const requestInfo = location?.state || {};
	const requestedQuantity = Number(requestInfo?.requestedQuantity) || 0;

	const [kitValues, setKitValues] = useState([""]);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [resultModal, setResultModal] = useState(null); // null | { state: "success" | "error", description }
	const [invalidReasons, setInvalidReasons] = useState({}); // { [index]: "پیام خطای همون باکس" }

	// تایمرهای debounce به‌ازای هر باکس، تا موقعی که گان کامل ۲۰ رقم رو نفرستاده باکس بعدی زودهنگام اضافه نشه
	const debounceTimers = useRef({});
	useEffect(() => {
		const timers = debounceTimers.current;
		return () => {
			Object.values(timers).forEach(clearTimeout);
		};
	}, []);

	// با اضافه‌شدن باکس جدید، نشانگر تایپ به‌صورت خودکار به همون باکس منتقل بشه
	const inputRefs = useRef([]);
	const prevKitCountRef = useRef(kitValues.length);
	useEffect(() => {
		if (kitValues.length > prevKitCountRef.current) {
			inputRefs.current[kitValues.length - 1]?.focus();
		}
		prevKitCountRef.current = kitValues.length;
	}, [kitValues.length]);

	// بافر خام هر باکس (بدون محدودیت طول) جدا از مقدار نمایشی نگه داشته می‌شه؛ چون مقدار نمایشی حداکثر ۹ رقمه
	// ولی اسکنر بارکد باید بتونه کل رشته‌ی ۲۰ رقمیش رو برسونه تا استخراج درست انجام بشه
	const rawBufferRef = useRef({});
	const lastDisplayLenRef = useRef({});

	const handleKitChange = (index) => (e) => {
		const domDigits = e.target.value.replace(/\D/g, "");
		const lastLen = lastDisplayLenRef.current[index] || 0;

		if (domDigits.length >= lastLen) {
			// فقط رقم‌های تازه‌ای که نسبت به مقدار نمایشی قبلی اضافه شدن رو به بافر خام اضافه کن
			rawBufferRef.current[index] = (rawBufferRef.current[index] || "") + domDigits.slice(lastLen);
		} else {
			// پاک کردن دستی (backspace/delete)؛ بافر خام رو با مقدار فعلی هم‌سطح کن
			rawBufferRef.current[index] = domDigits;
		}

		const rawDigits = rawBufferRef.current[index] || "";
		const value = extractKitNo(rawDigits);
		lastDisplayLenRef.current[index] = value.length;

		setInvalidReasons((prev) => {
			if (!prev[index]) return prev;
			const next = { ...prev };
			delete next[index];
			return next;
		});
		setKitValues((prev) => {
			const next = [...prev];
			next[index] = value;
			return next;
		});

		clearTimeout(debounceTimers.current[index]);
		debounceTimers.current[index] = setTimeout(() => {
			// فقط بعد از یه مکث کوتاه (یعنی تایپ/اسکن واقعاً تموم شده) تصمیم بگیر باکس بعدی لازمه یا نه
			setKitValues((prev) => {
				const current = prev[index];

				// اگه همین شماره کیت تو باکس دیگه‌ای هم هست (بیشتر موقع گان‌زدن پیش میاد)، تکراریه
				if (current?.length === 9) {
					const isDuplicate = prev.some((v, i) => i !== index && v === current);
					setInvalidReasons((prevReasons) => {
						const nextReasons = { ...prevReasons };
						if (isDuplicate) {
							nextReasons[index] = REASON_DUPLICATE;
						} else {
							delete nextReasons[index];
						}
						return nextReasons;
					});
				}

				if (index === prev.length - 1 && current?.length === 9 && prev.length < requestedQuantity) {
					return [...prev, ""];
				}
				return prev;
			});
		}, 150);
	};

	// وقتی از باکس خارج می‌شه (تب/کلیک بیرون)، اگه مقدارش پر ولی ناقص یا تکراری باشه بلافاصله قرمز بشه
	const handleKitBlur = (index) => () => {
		const value = kitValues[index];
		if (!value) return;
		const isIncomplete = value.length !== 9;
		const isDuplicate = !isIncomplete && kitValues.some((v, i) => i !== index && v === value);
		if (isIncomplete || isDuplicate) {
			setInvalidReasons((prev) => ({ ...prev, [index]: isIncomplete ? REASON_INCOMPLETE : REASON_DUPLICATE }));
		}
	};

	// حذف کامل یه باکس؛ همیشه حداقل یه باکس خالی باقی می‌مونه
	const handleRemoveBox = (index) => () => {
		setKitValues((prev) => {
			const next = prev.length <= 1 ? [""] : prev.filter((_, i) => i !== index);
			const nextReasons = next.reduce((acc, value, i) => {
				if (!value) return acc;
				const incomplete = value.length !== 9;
				const duplicate = !incomplete && next.some((v, j) => j !== i && v === value);
				if (incomplete) acc[i] = REASON_INCOMPLETE;
				else if (duplicate) acc[i] = REASON_DUPLICATE;
				return acc;
			}, {});
			setInvalidReasons(nextReasons);
			return next;
		});
		delete rawBufferRef.current[index];
		delete lastDisplayLenRef.current[index];
	};

	const handleSubmit = async () => {
		// باکسی که پر شده ولی به ۹ رقم نرسیده، نامعتبره و باید قرمز بشه
		const incompleteIndexes = kitValues.reduce((acc, value, index) => {
			if (value && value.length !== 9) {
				acc.push(index);
			}
			return acc;
		}, []);

		if (incompleteIndexes.length > 0) {
			setInvalidReasons((prev) => {
				const next = { ...prev };
				incompleteIndexes.forEach((i) => (next[i] = REASON_INCOMPLETE));
				return next;
			});
			setResultModal({
				state: "error",
				description: REASON_INCOMPLETE,
			});
			return;
		}

		// شماره کیت تکراری بین باکس‌ها مجاز نیست
		const duplicateIndexes = kitValues.reduce((acc, value, index) => {
			if (value && kitValues.some((v, i) => i !== index && v === value)) {
				acc.push(index);
			}
			return acc;
		}, []);

		if (duplicateIndexes.length > 0) {
			setInvalidReasons((prev) => {
				const next = { ...prev };
				duplicateIndexes.forEach((i) => (next[i] = REASON_DUPLICATE));
				return next;
			});
			setResultModal({
				state: "error",
				description: REASON_DUPLICATE,
			});
			return;
		}

		const kits = kitValues.filter((value) => value?.length === 9).map((value) => ({ kit: Number(value) }));

		if (kits.length < requestedQuantity) {
			setResultModal({
				state: "error",
				description: "تعداد کیت وارد شده کمتر از تعداد کیت درخواستی است.",
			});
			return;
		}

		setIsSubmitting(true);
		try {
			const payload = {
				requestKitId: requestInfo?.requsetId,
				kits,
			};

			// silent:true تا toast خودکار سرویس نشون داده نشه و خودمون با antd message پیام واقعی رو نمایش بدیم
			const response = await assignKitToDealer(payload, { silent: true });
			if (response && !response.HasError) {
				// بعد از ثبت موفق، کیف پول رو به‌روزرسانی کن تا مبلغ کمیسیونی که واریز شده مشخص بشه
				// طبق الگوی charge.jsx، واریز کمیسیون تو بک‌اند کمی طول می‌کشه؛ قبل از خوندن موجودی جدید یه مکث لازمه
				const balanceBeforeAssign = Number(_currentUser?.walletAmount) || 0;
				let commissionAmount = 0;
				await delay(3000);
				const updatedUser = await queryCurrentUser();
				if (updatedUser) {
					commissionAmount = (Number(updatedUser.walletAmount) || 0) - balanceBeforeAssign;
					dispatch(globalValue({ _currentUser: { ..._currentUser, ...updatedUser } }));
				}

				setResultModal({
					state: "success",
					description: (
						<>
							{response?.Message || "درخواست تخصیص کیت سیم‌کارت شما با موفقیت ثبت شد."}
							{commissionAmount > 0 && (
								<>
									<br />
									مبلغ <span dir="ltr">{complexDigitToman(commissionAmount)}</span> تومان کمیسیون به کیف پول شما واریز شد.
								</>
							)}
						</>
					),
				});
			} else {
				message.error(response?.Message || "خطایی رخ داد. دوباره تلاش کنید.");
			}
		} catch (error) {
			console.error("Failed to assign kits:", error);
			message.error("خطایی رخ داد. دوباره تلاش کنید.");
		}
		setIsSubmitting(false);
	};

	// دکمه‌ی تخصیص فقط وقتی فعاله که هیچ خطایی نباشه و دقیقاً به تعداد درخواستی، کیت معتبر وارد شده باشه
	const filledKitCount = kitValues.filter((value) => value?.length === 9).length;
	const isFormValid = Object.keys(invalidReasons).length === 0 && filledKitCount === requestedQuantity;

	const handleResultModalAction = () => {
		if (resultModal?.state === "success") {
			nav(_adr.Root + _adr.SimLocked.Dealer.Index + _adr.SimLocked.Dealer.AssignKit);
		} else {
			setResultModal(null);
		}
	};

	return (
		<div className="container-main container-cw">
			<div className="holder-top-of-page-content">
				<div className="g-hold-title-of-page">
					<Breadcrumb
						className="breadcrumbs"
						items={[
							{ href: _adr.Root + _adr.Home, title: <HomeOutlined /> },
							{ href: _adr.Root + _adr.SimLocked.Dealer.Index + _adr.SimLocked.Dealer.NewRequest, title: "مدیریت کیت سیم‌کارت" },
							{ href: _adr.Root + _adr.SimLocked.Dealer.Index + _adr.SimLocked.Dealer.AssignKit, title: "تخصیص کیت به نماینده" },
						]}
					/>
					<div className="title-page">
						<h3>تخصیص کیت به نماینده</h3>
						<p></p>
					</div>
				</div>
			</div>
			<div className="g-layout-content">
				<Content className="g-layout-main da-container">
					<div className="g-format-page">
						<div className="g-body-format-page">
							<Form layout="vertical">
								<Row gutter={16}>
									<Col xxl={6} xl={8} lg={10} md={12} sm={24} xs={24}>
										<Form.Item className="input-normal" label="تعداد کیت">
											<Input readOnly value={requestInfo?.requestedQuantity} />
										</Form.Item>
									</Col>
								</Row>
								<Row gutter={16}>
									<Col xxl={6} xl={8} lg={10} md={12} sm={24} xs={24}>
										<Form.Item className="input-normal" label="نوع مخزن">
											<Input readOnly value={requestInfo?.poolType} />
										</Form.Item>
									</Col>
								</Row>

								{kitValues.slice(0, requestedQuantity || kitValues.length).map((value, index) => (
									<Row gutter={16} key={index}>
										<Col xxl={6} xl={8} lg={10} md={12} sm={24} xs={24}>
											<Form.Item
												className="input-normal"
												validateStatus={invalidReasons[index] ? "error" : undefined}
												help={invalidReasons[index]}>
												<Input
													ref={(el) => (inputRefs.current[index] = el)}
													status={invalidReasons[index] ? "error" : undefined}
													dir="ltr"
													inputMode="numeric"
													maxLength={20}
													placeholder="شماره کیت"
													value={value}
													onChange={handleKitChange(index)}
													onBlur={handleKitBlur(index)}
													addonAfter={index + 1}
													suffix={
														<DeleteOutlined onClick={handleRemoveBox(index)} style={{ cursor: "pointer", color: "#999" }} />
													}
												/>
											</Form.Item>
										</Col>
									</Row>
								))}
							</Form>

							<div className="g-footer-format-page">
								<div className="holder-button">
									<Button
										className="btn-normal btn-aptel"
										type="primary"
										danger
										loading={isSubmitting}
										disabled={!isFormValid}
										onClick={handleSubmit}>
										تخصیص کیت
									</Button>
									<Button
										className="btn-normal"
										type="default"
										onClick={() => nav(_adr.Root + _adr.SimLocked.Dealer.Index + _adr.SimLocked.Dealer.AssignKit)}>
										بازگشت
									</Button>
								</div>
							</div>
						</div>
					</div>
				</Content>
			</div>

			<KitRequestModal
				open={Boolean(resultModal)}
				state={resultModal?.state}
				title="درخواست تخصیص کیت سیم‌کارت"
				description={resultModal?.description}
				buttonText={resultModal?.state === "success" ? "متوجه شدم" : "ویرایش"}
				onClose={() => setResultModal(null)}
				onAction={handleResultModalAction}
			/>
		</div>
	);
};

export default AssignKitDetail;
